import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronRight, Clock, Sparkles, Feather, Sunrise, Sprout, Users, User, Heart, Leaf, Lightbulb, Wind, Bird, Wrench, ImageOff } from "lucide-react";
import axios from "axios";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SocialButtons } from "@/components/SocialButtons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" } }),
};

function whatsappLink(serviceName) {
  const msg = encodeURIComponent(`Hi Joyce, I'm interested in ${serviceName}. Could we discuss?`);
  return `https://wa.me/6597946649?text=${msg}`;
}

function HeroSection({ heroImage }) {
  return (
    <section className="relative min-h-[90vh] flex items-center pt-24 pb-16" data-testid="hero-section">
      <div className="max-w-6xl mx-auto px-6 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 space-y-8">
            <motion.h1
              initial="hidden" animate="visible" variants={fadeUp} custom={0}
              className="font-heading text-5xl md:text-6xl lg:text-7xl font-semibold text-charcoal leading-[1.1] tracking-tight"
            >
              What you're experiencing<br />
              <span className="italic text-navy">isn't random.</span>
            </motion.h1>
            <motion.p
              initial="hidden" animate="visible" variants={fadeUp} custom={1}
              className="font-body text-lg text-charcoal-light leading-relaxed max-w-xl"
            >
              I help you understand and shift the patterns behind your emotions,
              relationships, and life challenges.
            </motion.p>
            <motion.p
              initial="hidden" animate="visible" variants={fadeUp} custom={2}
              className="font-body text-base text-charcoal-light/80 max-w-xl"
            >
              Using BodyTalk, Talent Numerology, VMLD (Veltheim Method Lymphatic Drainage),
              and conscious coaching with PET.
            </motion.p>
            <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={3} className="flex flex-wrap gap-4">
              <a
                href="https://wa.me/6597946649"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-book-btn"
                className="inline-flex items-center gap-2 bg-navy text-white px-8 py-4 rounded-full font-body font-semibold text-lg hover:bg-navy-light transition-all hover:scale-105 shadow-soft"
              >
                Book a Session <ArrowRight className="w-5 h-5" />
              </a>
            </motion.div>
            <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={4}>
              <SocialButtons variant="compact" />
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative rounded-[2rem] overflow-hidden shadow-lift aspect-[4/5] bg-champagne-dark">
              {heroImage ? (
                <img src={heroImage} alt="Journey With Joyce Lim" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Leaf className="w-16 h-16 text-navy/15" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-champagne/30 to-transparent" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function WhoThisIsFor({ joycePhoto }) {
  const items = [
    "You feel stuck, overwhelmed, or emotionally drained, and nothing you've tried seems to fully resolve it",
    "You're functioning on the surface, but internally, something feels off or disconnected",
    "You want clarity — not just advice, but a deeper understanding of what's really going on",
    "You're a parent trying to support your child, but finding yourself frustrated, confused, or repeating patterns you don't want",
    "Your child or teen is struggling emotionally or behaviorally, and you know there's more beneath the surface",
    "You're starting to notice recurring patterns in your life or relationships — and you're ready to understand and change them",
  ];

  return (
    <section className="py-24 bg-white" data-testid="who-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch">
          {/* Joyce's photo — stretches full height of section */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-4 flex"
          >
            <div className="rounded-[2rem] overflow-hidden shadow-lift w-full">
              {joycePhoto ? (
                <img src={joycePhoto} alt="Joyce Lim" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full min-h-[400px] bg-champagne-dark flex items-center justify-center">
                  <ImageOff className="w-12 h-12 text-soft-grey" />
                </div>
              )}
            </div>
          </motion.div>

          {/* Content */}
          <div className="lg:col-span-8 flex flex-col">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-charcoal mb-4">
                Who I Work With
              </h2>
              <p className="font-body text-charcoal-light text-lg mb-8 max-w-2xl">
                I work with individuals and families who feel that something isn't quite right — even if they can't fully explain why.
              </p>
            </motion.div>
            <div className="space-y-3 flex-1">
              {items.map((item, i) => (
                <motion.div
                  key={i}
                  initial="hidden" whileInView="visible" viewport={{ once: true }}
                  variants={fadeUp} custom={i}
                  className="px-6 py-4 rounded-2xl border border-border hover:shadow-soft transition-all bg-off-white w-full"
                >
                  <p className="font-body text-charcoal leading-relaxed">{item}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatternShiftMethod() {
  const steps = [
    {
      icon: Sparkles,
      title: "Reveal",
      desc: "We identify what's actually driving your experience. Using Talent Numerology and guided insight, we uncover your traits, emotional patterns, and triggers — so what felt confusing becomes clearer and easier to understand.",
    },
    {
      icon: Feather,
      title: "Release",
      desc: "Clarity alone is not enough. Through BodyTalk and VMLD (Veltheim Method Lymphatic Drainage), we work to reduce stored emotional and physical stress — supporting the body in returning to a more balanced state.",
    },
    {
      icon: Sunrise,
      title: "Reframe",
      desc: "With clearer awareness and reduced internal stress, your perspective begins to shift. You start to see your patterns and reactions with more understanding, which creates space to respond more thoughtfully rather than automatically.",
    },
    {
      icon: Sprout,
      title: "Rebuild",
      desc: "Change takes practice. You will learn practical techniques you can use on your own — at home and in real situations — to support emotional regulation, reinforce new responses, and build greater awareness over time. The focus is not on perfection, but on having tools you can return to consistently.",
    },
  ];

  const outcomes = [
    { text: "Better understanding of your patterns and responses", icon: Lightbulb },
    { text: "Reduced overwhelm and clearer thinking", icon: Wind },
    { text: "More space to choose how you respond", icon: Bird },
    { text: "Practical tools you can continue using beyond sessions", icon: Wrench },
  ];

  return (
    <section className="py-24" data-testid="method-section">
      <div className="max-w-6xl mx-auto px-6">
        {/* Title */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-10">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Leaf className="w-8 h-8 text-navy" />
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-charcoal">
              The Pattern Shift Method
            </h2>
          </div>
        </motion.div>

        {/* Impact statement — styled as a distinct callout */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="max-w-3xl mx-auto mb-16 bg-white/80 border-l-4 border-navy/30 rounded-r-2xl px-8 py-8 shadow-soft"
        >
          <p className="font-heading text-xl md:text-2xl text-charcoal leading-snug mb-3">
            Most people try to fix what they can see — behavior, reactions, situations.
          </p>
          <p className="font-heading text-xl md:text-2xl text-navy italic leading-snug mb-5">
            But real change doesn't happen there.<br />
            It begins with understanding the patterns underneath.
          </p>
          <p className="font-body text-charcoal-light leading-relaxed">
            This is the process I use to help you make sense of what's happening, reduce internal stress,
            and respond differently — in a way you can continue to apply in daily life.
          </p>
        </motion.div>

        {/* 4 Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {steps.map((step, i) => (
            <motion.div
              key={i}
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={fadeUp} custom={i}
              className="relative bg-white border border-border rounded-2xl p-8 hover:shadow-lift transition-all group"
            >
              <div className="flex items-start gap-5">
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 bg-champagne rounded-2xl flex items-center justify-center group-hover:bg-navy/10 transition-colors">
                    <step.icon className="w-7 h-7 text-navy/70" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-baseline gap-3 mb-3">
                    <span className="font-heading text-3xl font-bold text-navy/15">{i + 1}</span>
                    <h3 className="font-heading text-xl font-semibold text-charcoal">{step.title}</h3>
                  </div>
                  <p className="font-body text-sm text-charcoal-light leading-relaxed">{step.desc}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* What This Means for You — as icon cards */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="mt-16"
        >
          <h3 className="font-heading text-2xl font-semibold text-charcoal text-center mb-8">What This Means for You</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {outcomes.map((item, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}
                className="bg-white border border-border rounded-2xl p-6 text-center hover:shadow-soft transition-all"
              >
                <div className="w-10 h-10 bg-champagne rounded-xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-5 h-5 text-navy/60" />
                </div>
                <p className="font-body text-sm text-charcoal leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a
              href="https://wa.me/6597946649"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-navy text-white px-7 py-3 rounded-full font-body font-semibold hover:bg-navy-light transition-all hover:scale-105 shadow-soft"
            >
              Book a Session <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="https://wa.me/6597946649"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border-2 border-navy text-navy px-7 py-3 rounded-full font-body font-semibold hover:bg-navy/5 transition-all"
            >
              Start with an Initial Consultation
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function ServicesPreview({ services }) {
  const individuals = services.filter((s) => s.audience === "individual");
  const families = services.filter((s) => s.audience === "family");

  const ServiceCard = ({ svc }) => {
    const imgUrl = svc.image_url;
    return (
    <a
      href={whatsappLink(svc.name)}
      target="_blank" rel="noopener noreferrer"
      data-testid={`service-card-${svc.slug}`}
      className="group block bg-white border border-border rounded-2xl hover:shadow-lift transition-all duration-300 h-full overflow-hidden"
    >
      <div className="h-40 overflow-hidden">
        {imgUrl ? (
          <img src={imgUrl} alt={svc.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full bg-champagne-dark flex items-center justify-center">
            <ImageOff className="w-8 h-8 text-soft-grey" />
          </div>
        )}
      </div>
      <div className="p-6">
        <h3 className="font-heading text-lg font-semibold text-charcoal mb-2 group-hover:text-navy transition-colors">
          {svc.name}
        </h3>
        <p className="font-body text-sm text-charcoal-light leading-relaxed mb-3">
          {svc.short_description}
        </p>
        <div className="flex items-center gap-4 font-body text-xs text-charcoal-light">
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" /> {svc.duration_minutes} min
          </span>
          {svc.price > 0 && (
            <span className="font-semibold text-navy">
              {svc.currency} {svc.price}
            </span>
          )}
        </div>
      </div>
    </a>
  );
  };

  return (
    <section className="py-24 bg-white" data-testid="services-preview-section">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-charcoal mb-4">
            Work With Me
          </h2>
          <p className="font-body text-charcoal-light text-lg max-w-2xl mx-auto">
            Services grouped by who they're designed for. Every session is tailored to your specific needs.
          </p>
        </motion.div>

        {individuals.length > 0 && (
          <div className="mb-12">
            <motion.h3 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="font-heading text-2xl font-semibold text-charcoal mb-6 flex items-center gap-2"
            >
              <User className="w-5 h-5 text-navy" /> For Individuals
            </motion.h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {individuals.map((svc, i) => (
                <motion.div key={svc.id} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}>
                  <ServiceCard svc={svc} />
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {families.length > 0 && (
          <div>
            <motion.h3 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="font-heading text-2xl font-semibold text-charcoal mb-6 flex items-center gap-2"
            >
              <Users className="w-5 h-5 text-navy" /> For Families & Parents
            </motion.h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {families.map((svc, i) => (
                <motion.div key={svc.id} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}>
                  <ServiceCard svc={svc} />
                </motion.div>
              ))}
            </div>
          </div>
        )}

        <div className="text-center mt-12">
          <Link
            to="/work-with-me"
            data-testid="services-view-all-btn"
            className="inline-flex items-center gap-2 border-2 border-navy text-navy px-8 py-3 rounded-full font-body font-semibold hover:bg-navy/5 transition-all"
          >
            View All Services <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function JournalPreview({ journalImage }) {
  return (
    <section className="py-24" data-testid="journal-preview-section">
      <div className="max-w-5xl mx-auto px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="bg-white border border-border rounded-3xl overflow-hidden"
        >
          <div className="grid grid-cols-1 md:grid-cols-12 gap-0">
            <div className="md:col-span-5 h-64 md:h-auto">
              {journalImage ? (
                <img src={journalImage} alt="Guided Emotional Journal" className="w-full h-full object-cover min-h-[280px]" />
              ) : (
                <div className="w-full h-full min-h-[280px] bg-champagne-dark flex items-center justify-center">
                  <ImageOff className="w-10 h-10 text-soft-grey" />
                </div>
              )}
            </div>
            <div className="md:col-span-7 p-10 md:p-14 flex flex-col justify-center">
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-charcoal mb-4">
                A Structured Journal for Thinking & Growth
              </h2>
              <div className="font-body text-charcoal-light leading-relaxed text-lg mb-6 space-y-4">
                <p>This is not just a place to write.</p>
                <p>It is a simple, structured system designed to help you understand your thoughts, emotions, and behaviours through daily reflection.</p>
                <p>Using practical frameworks and guided sections, it supports clearer thinking, greater awareness, and more intentional responses in everyday life.</p>
                <p>Designed for children, teens, and adults, it can be used independently or alongside guidance.</p>
              </div>
              <Link
                to="/journal"
                data-testid="journal-preview-btn"
                className="inline-flex items-center gap-2 text-navy font-body font-semibold hover:gap-3 transition-all"
              >
                Learn more about the Journal <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function TestimonialScroll() {
  const [testimonials, setTestimonials] = useState([]);
  const trackRef = useRef(null);
  const animRef = useRef(null);
  const posRef = useRef(0);

  useEffect(() => {
    axios.get(`${API}/testimonials?page=1&limit=50`).then((r) => {
      setTestimonials(r.data.items || []);
    }).catch(console.error);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || testimonials.length === 0) return;

    const speed = 0.5;
    let paused = false;

    const animate = () => {
      if (!paused) {
        posRef.current -= speed;
        const halfWidth = track.scrollWidth / 2;
        if (Math.abs(posRef.current) >= halfWidth) {
          posRef.current = 0;
        }
        track.style.transform = `translateX(${posRef.current}px)`;
      }
      animRef.current = requestAnimationFrame(animate);
    };

    animRef.current = requestAnimationFrame(animate);

    const onEnter = () => { paused = true; };
    const onLeave = () => { paused = false; };
    track.addEventListener("mouseenter", onEnter);
    track.addEventListener("mouseleave", onLeave);

    return () => {
      cancelAnimationFrame(animRef.current);
      track.removeEventListener("mouseenter", onEnter);
      track.removeEventListener("mouseleave", onLeave);
    };
  }, [testimonials]);

  if (!testimonials.length) return null;

  const marqueeItems = [...testimonials, ...testimonials];

  return (
    <section className="py-24 overflow-hidden bg-white" data-testid="testimonials-section">
      <div className="max-w-6xl mx-auto px-6 mb-12">
        <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="font-heading text-3xl md:text-4xl font-semibold text-charcoal"
        >
          What Clients Experience
        </motion.h2>
      </div>

      <div className="relative" style={{ maskImage: "linear-gradient(to right, transparent, black 5%, black 95%, transparent)", WebkitMaskImage: "linear-gradient(to right, transparent, black 5%, black 95%, transparent)" }}>
        <div ref={trackRef} className="flex gap-8 will-change-transform" style={{ width: "max-content" }}>
          {marqueeItems.map((t, i) => (
            <div
              key={`${t.id}-${i}`}
              data-testid={`testimonial-card-${i}`}
              className="flex-shrink-0 w-[350px] md:w-[420px] bg-off-white border border-border p-8 rounded-2xl"
            >
              <p className="font-heading text-lg italic text-charcoal leading-relaxed mb-6">
                &ldquo;{t.quote}&rdquo;
              </p>
              <div>
                <p className="font-body text-sm font-semibold text-navy">{t.client_name}</p>
                {t.client_description && (
                  <p className="font-body text-xs text-charcoal-light mt-1">{t.client_description}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaSection() {
  return (
    <section className="py-24" data-testid="cta-section">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-charcoal mb-6">
            Understanding is the first step to change.
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://wa.me/6597946649"
              target="_blank" rel="noopener noreferrer"
              data-testid="cta-book-btn"
              className="inline-flex items-center gap-2 bg-navy text-white px-8 py-4 rounded-full font-body font-semibold text-lg hover:bg-navy-light transition-all hover:scale-105 shadow-soft"
            >
              Book a Session <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href="https://wa.me/6597946649"
              target="_blank" rel="noopener noreferrer"
              data-testid="cta-whatsapp-btn"
              className="inline-flex items-center gap-2 border-2 border-navy text-navy px-8 py-4 rounded-full font-body font-semibold text-lg hover:bg-navy/5 transition-all"
            >
              WhatsApp Joyce
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [siteImages, setSiteImages] = useState(null);

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => setServices(r.data)).catch(console.error);
    axios.get(`${API}/site-settings`).then((r) => setSiteImages(r.data)).catch(console.error);
  }, []);

  if (!siteImages) {
    return (
      <div className="min-h-screen bg-champagne" data-testid="home-page">
        <Navbar />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-champagne" data-testid="home-page">
      <Navbar />
      <HeroSection heroImage={siteImages.hero_image} />
      <WhoThisIsFor joycePhoto={siteImages.joyce_photo} />
      <PatternShiftMethod />
      <ServicesPreview services={services} />
      <JournalPreview journalImage={siteImages.journal_image} />
      <TestimonialScroll />
      <CtaSection />
      <Footer />
    </div>
  );
}
